import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { LedgerEntry } from '@/types';
import { Button } from '@/components/ui/button';
import {
    DollarSign,
    TrendingUp,
    Receipt,
    Landmark,
    Wrench,
    Skull,
    FileText,
    MessageSquare,
    X,
    Check,
} from 'lucide-react';

const typeConfig: Record<string, { icon: typeof DollarSign; color: string; bgColor: string; borderColor: string }> = {
    payment: { icon: DollarSign, color: 'text-emerald-400', bgColor: 'bg-emerald-900/30', borderColor: 'border-emerald-800' },
    interest: { icon: TrendingUp, color: 'text-red-400', bgColor: 'bg-red-900/30', borderColor: 'border-red-800' },
    fee: { icon: Receipt, color: 'text-orange-400', bgColor: 'bg-orange-900/30', borderColor: 'border-orange-800' },
    tax: { icon: Landmark, color: 'text-purple-400', bgColor: 'bg-purple-900/30', borderColor: 'border-purple-800' },
    origination_fee: { icon: FileText, color: 'text-violet-400', bgColor: 'bg-violet-900/30', borderColor: 'border-violet-800' },
    adjustment: { icon: Wrench, color: 'text-slate-400', bgColor: 'bg-slate-800', borderColor: 'border-slate-700' },
    abandonment_penalty: { icon: Skull, color: 'text-red-500', bgColor: 'bg-red-900/40', borderColor: 'border-red-800' },
};

interface PaymentTimelineProps {
    entries: LedgerEntry[];
    contractId: number;
    isDom: boolean;
}

function groupByDate(entries: LedgerEntry[]): Map<string, LedgerEntry[]> {
    const groups = new Map<string, LedgerEntry[]>();
    for (const entry of entries) {
        const dateKey = new Date(entry.effective_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
        const existing = groups.get(dateKey) ?? [];
        existing.push(entry);
        groups.set(dateKey, existing);
    }
    return groups;
}

function TimelineEntry({
    entry,
    contractId,
    isDom,
    isLast,
}: {
    entry: LedgerEntry;
    contractId: number;
    isDom: boolean;
    isLast: boolean;
}) {
    const [editingNote, setEditingNote] = useState(false);
    const [noteText, setNoteText] = useState(entry.notes ?? '');
    const queryClient = useQueryClient();

    const noteMutation = useMutation({
        mutationFn: (notes: string | null) =>
            api.put(`/contracts/${contractId}/ledger/${entry.id}/note`, { notes }),
        onSuccess: () => {
            setEditingNote(false);
            queryClient.invalidateQueries({ queryKey: ['ledger', String(contractId)] });
        },
    });

    const config = typeConfig[entry.type] ?? typeConfig.adjustment;
    const Icon = config.icon;
    const amount = parseFloat(entry.amount);
    const isNegative = amount < 0;

    return (
        <div className="flex gap-3">
            {/* Timeline line + dot */}
            <div className="flex flex-col items-center">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.bgColor}`}>
                    <Icon className={`h-4 w-4 ${config.color}`} />
                </div>
                {!isLast && <div className="w-px flex-1 bg-slate-800" />}
            </div>

            {/* Content */}
            <div className={`mb-4 flex-1 rounded-lg border ${config.borderColor} bg-slate-900/50 p-3`}>
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-200">
                            {entry.description}
                        </p>
                        <p className="text-[10px] text-slate-500">
                            {new Date(entry.effective_at).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                            })}
                            {' '}
                            <span className="capitalize">{entry.type.replace('_', ' ')}</span>
                            {entry.source !== 'system' && entry.created_by && (
                                <> by {entry.created_by.display_name}</>
                            )}
                        </p>
                    </div>
                    <div className="text-right shrink-0">
                        <p className={`text-sm font-bold ${config.color}`}>
                            {isNegative ? '-' : '+'}
                            {formatCurrency(Math.abs(amount))}
                        </p>
                        <p className="text-[10px] text-slate-500">
                            Bal: {formatCurrency(entry.balance_after)}
                        </p>
                    </div>
                </div>

                {/* Notes display */}
                {entry.notes && !editingNote && (
                    <div className="mt-2 flex items-start gap-1.5 rounded bg-slate-800/50 px-2 py-1.5">
                        <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-violet-400" />
                        <p className="text-xs text-slate-300">{entry.notes}</p>
                    </div>
                )}

                {/* Note editing */}
                {editingNote && (
                    <div className="mt-2 space-y-2">
                        <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Add a note or commentary..."
                            className="w-full rounded border border-slate-700 bg-slate-800 px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-violet-500"
                            rows={2}
                        />
                        <div className="flex gap-1">
                            <Button
                                variant="secondary"
                                size="sm"
                                className="h-6 px-2 text-[10px]"
                                disabled={noteMutation.isPending}
                                onClick={() => noteMutation.mutate(noteText.trim() || null)}
                            >
                                <Check className="mr-1 h-3 w-3" />
                                {noteMutation.isPending ? 'Saving...' : 'Save'}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-[10px]"
                                onClick={() => {
                                    setEditingNote(false);
                                    setNoteText(entry.notes ?? '');
                                }}
                            >
                                <X className="mr-1 h-3 w-3" />
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}

                {/* Add/edit note button (dom only) */}
                {isDom && !editingNote && (
                    <button
                        className="mt-1.5 text-[10px] text-slate-600 hover:text-violet-400 transition-colors"
                        onClick={() => {
                            setNoteText(entry.notes ?? '');
                            setEditingNote(true);
                        }}
                    >
                        {entry.notes ? 'Edit note' : '+ Add note'}
                    </button>
                )}
            </div>
        </div>
    );
}

export function PaymentTimeline({ entries, contractId, isDom }: PaymentTimelineProps) {
    const [filter, setFilter] = useState<string | null>(null);

    const filtered = filter ? entries.filter((e) => e.type === filter) : entries;
    const grouped = groupByDate(filtered);

    const entryTypes = [...new Set(entries.map((e) => e.type))];

    return (
        <div>
            {/* Filter pills */}
            <div className="mb-4 flex flex-wrap gap-1.5">
                <button
                    className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                        !filter
                            ? 'bg-violet-900/50 text-violet-300'
                            : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                    }`}
                    onClick={() => setFilter(null)}
                >
                    All ({entries.length})
                </button>
                {entryTypes.map((type) => {
                    const config = typeConfig[type] ?? typeConfig.adjustment;
                    const count = entries.filter((e) => e.type === type).length;
                    return (
                        <button
                            key={type}
                            className={`rounded-full px-2.5 py-1 text-[10px] font-medium transition-colors ${
                                filter === type
                                    ? `${config.bgColor} ${config.color}`
                                    : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                            }`}
                            onClick={() => setFilter(filter === type ? null : type)}
                        >
                            {type.replace('_', ' ')} ({count})
                        </button>
                    );
                })}
            </div>

            {/* Timeline */}
            {filtered.length === 0 ? (
                <p className="text-sm text-slate-500">No entries match the filter.</p>
            ) : (
                <div>
                    {Array.from(grouped.entries()).map(([dateLabel, dateEntries]) => (
                        <div key={dateLabel}>
                            {/* Date header */}
                            <div className="mb-2 flex items-center gap-2">
                                <div className="h-px flex-1 bg-slate-800" />
                                <span className="text-[10px] font-medium text-slate-500">
                                    {dateLabel}
                                </span>
                                <div className="h-px flex-1 bg-slate-800" />
                            </div>

                            {/* Entries for this date */}
                            {dateEntries.map((entry, i) => (
                                <TimelineEntry
                                    key={entry.id}
                                    entry={entry}
                                    contractId={contractId}
                                    isDom={isDom}
                                    isLast={
                                        i === dateEntries.length - 1 &&
                                        dateLabel === Array.from(grouped.keys()).pop()
                                    }
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
